// Broadband and mobile coverage from Ofcom's Connected Nations data, as the area overview
// carries it (overview.connectivity, ukmap.get_overview: {oa, lad, rgn, ctry, release}),
// with the postcode's own broadband (the /propertylookup "area" request's broadband) and,
// on the buyer's and renter's reports, the postcode's mobile coverage from Ofcom's API
// (/propertylookup "mobile"). Shown by ConnectivityBlock in ListingArea.js and the PDFs.

export const NETWORKS = ['EE', 'O2', 'Three', 'Vodafone'];

// Broadband speeds, slowest first, by their key in Ofcom's figures (% of premises able to get them)
export const SPEED_TIERS = [
    ['sfbb', 'Superfast', '30 Mbit/s or more'],
    ['ufbb100', '100 Mbit/s', '100 Mbit/s or more'],
    ['ufbb', 'Ultrafast', '300 Mbit/s or more'],
    ['gigabit', 'Gigabit', 'about 1,000 Mbit/s'],
];

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const has = (v) => v !== null && v !== undefined && v !== '' && !isNaN(Number(v));
const round = (v) => Math.round(Number(v));

// '202601' -> 'January 2026', or '' for anything else
export const releaseText = (yyyymm) => {
    const m = /^(\d{4})(\d{2})$/.exec(String(yyyymm || ''));
    if (!m || Number(m[2]) < 1 || Number(m[2]) > 12) return '';
    return MONTHS[Number(m[2]) - 1] + ' ' + m[1];
};

// The overview's connectivity, or {} when there is none
const conn = (o) => (o && o.connectivity) || {};

// Whether there is anything to show
export const hasConnectivity = (o, broadband, mobile) => {
    const c = conn(o);
    return !!(broadband && Object.keys(broadband).length) || !!(c.oa && c.oa.broadband) ||
        !!(c.lad && (c.lad.broadband || c.lad.mobile)) || !!(mobile && mobile.addresses > 0);
};

// The most local broadband figures there are: the postcode's, else the output area's
// ({where: 'postcode' | 'area', bb}), or null
export const localBroadband = (o, broadband) => {
    if (broadband && SPEED_TIERS.some(([k]) => has(broadband[k]))) return {where:'postcode', bb:broadband};
    const oa = conn(o).oa;
    if (oa && oa.broadband && SPEED_TIERS.some(([k]) => has(oa.broadband[k]))) return {where:'area', bb:oa.broadband};
    return null;
};

// One line saying what broadband the home can get: its fastest tier most premises there can get
export const broadbandHeadline = (local) => {
    if (!local) return null;
    const {bb, where} = local;
    const here = where === 'postcode' ? 'at this postcode' : 'in the few streets around it';
    const gig = has(bb.gigabit) ? round(bb.gigabit) : null;
    if (gig !== null && gig >= 50) return `Gigabit-capable broadband reaches ${gig}% of homes and businesses ${here}.`;
    // The fastest tier most premises can get
    const best = SPEED_TIERS.slice().reverse().find(([k]) => has(bb[k]) && Number(bb[k]) >= 50);
    if (best) return `${best[1]} broadband (${best[2]}) reaches ${round(bb[best[0]])}% of homes and businesses ${here}` +
        (gig !== null ? `; gigabit, ${gig}%.` : '.');
    if (has(bb.sfbb)) return `Only ${round(bb.sfbb)}% of homes and businesses ${here} can get superfast broadband (30 Mbit/s or more).`;
    return null;
};

// A warning when some premises can't get decent broadband (10 Mbit/s, the legal minimum), or null
export const slowBroadbandNote = (local) => {
    if (!local || !has(local.bb.under10) || Number(local.bb.under10) < 1) return null;
    const here = local.where === 'postcode' ? 'at this postcode' : 'in these streets';
    return `${round(local.bb.under10)}% of premises ${here} can't get 10 Mbit/s, the legal minimum for decent broadband.`;
};

// Gigabit coverage here against the local authority, region and country, as Bars rows
// ({key, label, value}); colours are added by the page
export const gigabitRows = (o, broadband) => {
    const c = conn(o);
    const d = (o && o.details) || {};
    const rows = [];
    if (broadband && has(broadband.gigabit)) rows.push({key:'pc', label:'This postcode', value:Number(broadband.gigabit)});
    if (c.oa && c.oa.broadband && has(c.oa.broadband.gigabit)) rows.push({key:'oa', label:'Local area', value:Number(c.oa.broadband.gigabit)});
    [['lad', d.lad25nm || 'Local authority'], ['rgn', d.rgn25nm || 'Region'], ['ctry', d.ctry25nm || 'Country']].forEach(([k, label]) => {
        const a = c[k];
        if (a && a.broadband && has(a.broadband.gigabit)) rows.push({key:k, label:a.areaname || label, value:Number(a.broadband.gigabit)});
    });
    return rows;
};

// Each speed tier's share of premises at the most local level, as Bars rows
export const tierRows = (local) => !local ? [] : SPEED_TIERS.filter(([k]) => has(local.bb[k]))
    .map(([k, label, what]) => ({key:k, label, extra:what, value:Number(local.bb[k])}));

// Actual speeds lines get (Ofcom's fixed broadband performance), most local first:
// [{label, median, average}] for those with a figure
export const speedRows = (o, broadband) => {
    const c = conn(o);
    const d = (o && o.details) || {};
    const rows = [];
    const add = (label, bb) => {
        if (bb && (has(bb.down_med) || has(bb.down_avg))) {
            rows.push({label, median:has(bb.down_med) ? Number(bb.down_med) : null, average:has(bb.down_avg) ? Number(bb.down_avg) : null});
        }
    };
    add('This postcode', broadband);
    add('Local area', c.oa && c.oa.broadband);
    add((c.lad && c.lad.areaname) || d.lad25nm || 'Local authority', c.lad && c.lad.broadband);
    add((c.ctry && c.ctry.areaname) || d.ctry25nm || 'Country', c.ctry && c.ctry.broadband);
    return rows;
};

// Ofcom's text that its API terms of use (2025, clause 4.2.2) require next to its mobile
// coverage, word for word
export const OFCOM_MOBILE_DISCLAIMER = ['The data displayed on mobile coverage availability uses source data made available by Ofcom which is based on data from the mobile operators about how strong they think signal levels are at every location in the UK. Each mobile operator has a slightly different approach to displaying coverage on its own map, including assumptions on the handsets used, levels of call reliability and the expected signal loss when indoors or in car. Because Ofcom brings all mobile operator data together in a single place and holds it to a single, independent standard, the mobile coverage availability information above may display different levels of coverage than those seen on the operators\' websites. We would therefore recommend you also check the mobile provider\'s coverage checker. Mobile coverage data is created by coverage modelling, and since this is based on computer predictions is not error free.',
    'Ofcom update the source data quarterly and the mobile network operators update theirs, but there may be times when the maps are based on slightly different data and therefore show different coverage.'];

// Mobile coverage per network. From Ofcom's API for the home itself when the answer has it
// ({where: 'home', ...} as below, with no counts), else for the postcode's addresses when there is an
// answer with addresses: Ofcom's own descriptor for 4G and 5G (shown unchanged, as its terms
// require) that most addresses have, {where: 'postcode', name, addresses, descriptors: true,
// rows: [{network, g4: {descriptor, count}, g5}]}. Else the local authority's Connected
// Nations figures (share of premises): {where: 'lad', name, rows: [{network, in4g, out4g, out5g}]}.
// Null with neither
export const mobileTable = (o, mobile) => {
    const pick = (m, k) => m && has(m[k]) ? Number(m[k]) : null;
    const desc = (g) => g && g.descriptor ? {descriptor:String(g.descriptor), count:has(g.count) ? Number(g.count) : null} : null;
    // The home's own, when Ofcom has its address
    const home = mobile && mobile.home && mobile.home.networks;
    if (home) {
        const one = (d) => d ? {descriptor:String(d), count:null} : null;
        const rows = NETWORKS.filter((n) => home[n]).map((n) => ({network:n, g4:one(home[n]['4g']), g5:one(home[n]['5g'])}))
            .filter((r) => r.g4 || r.g5);
        if (rows.length) return {where:'home', name:mobile.postcode, addresses:mobile.addresses, descriptors:true, rows};
    }
    if (mobile && mobile.addresses > 0 && mobile.networks) {
        const rows = NETWORKS.filter((n) => mobile.networks[n]).map((n) => ({network:n, g4:desc(mobile.networks[n]['4g']), g5:desc(mobile.networks[n]['5g'])}))
            .filter((r) => r.g4 || r.g5);
        if (rows.length) return {where:'postcode', name:mobile.postcode, addresses:mobile.addresses, descriptors:true, rows};
    }
    const lad = conn(o).lad;
    if (lad && lad.mobile) {
        const rows = NETWORKS.filter((n) => lad.mobile[n]).map((n) => {
            const m = lad.mobile[n];
            return {network:n, in4g:pick(m, '4g_in'), out4g:pick(m, '4g_out'), out5g:pick(m, '5g_out')};
        }).filter((r) => r.in4g !== null || r.out4g !== null || r.out5g !== null);
        if (rows.length) return {where:'lad', name:lad.areaname || (o.details && o.details.lad25nm) || 'the local authority', rows};
    }
    return null;
};

// The local authority's share of premises with 4G indoors from all four networks, and from none
export const allNetworks = (o) => {
    const lad = conn(o).lad;
    const m = lad && lad.mobile;
    if (!m) return null;
    const all = (m.n4 && has(m.n4['4g_in'])) ? Number(m.n4['4g_in']) : (m.all && has(m.all['4g_in'])) ? Number(m.all['4g_in']) : null;
    const none = (m.n0 && has(m.n0['4g_in'])) ? Number(m.n0['4g_in']) : null;
    return all === null && none === null ? null : {all, none};
};

// The source line, with the months the figures are from
export const sourceText = (o, mobileFromApi) => {
    const r = conn(o).release || {};
    const fixed = releaseText(r.fixed), mob = releaseText(r.mobile), perf = releaseText(r.perf);
    const parts = [];
    parts.push('Ofcom Connected Nations' + (fixed ? ', broadband ' + fixed : ''));
    if (perf) parts.push('speeds ' + perf);
    if (mobileFromApi) parts.push('mobile from Ofcom\'s API');
    else if (mob) parts.push('mobile ' + mob);
    return parts.join(', ') + '.';
};

// A coverage share as text: whole numbers, but "over 99%" and "under 1%" rather than rounding to 100 or 0
export const coverageText = (v) => {
    if (!has(v)) return '–';
    const n = Number(v);
    if (n > 99 && n < 100) return '99%+';
    if (n > 0 && n < 1) return '<1%';
    return round(n) + '%';
};
